import { isHttpStatusError } from "@sg-protect/core";
import type { Region } from "@sg-protect/runtime-config";
import { useMutation as useTanstackMutation } from "@tanstack/react-query";
import { client, queryOptions } from "../client.ts";
import { protectRegionValues } from "../types.ts";
import { fromProtectRegion, toProtectRegion } from "@sg-protect/shell";

export function protectProfileQueryOptions() {
    return queryOptions("get", "/bff/subscription/protect-profile");
}

export interface SetProtectRegionResult {
    region: Region;
    wasAccepted: boolean;
}

export function useSetProtectRegionMutation() {
    return useTanstackMutation({
        mutationFn: (region: Region) => setProtectRegion(region)
    });
}

// The client throws on every non-2xx response and copies the body onto the error, so a 409 carries the saved region.
function getConflictRegion(error: unknown): Region | null {
    if (!isHttpStatusError(error, 409) || !(error instanceof Error) || !("region" in error)) {
        return null;
    }

    const savedRegion = protectRegionValues.find(value => value === error.region);

    return savedRegion ? fromProtectRegion(savedRegion) : null;
}

async function setProtectRegion(region: Region): Promise<SetProtectRegionResult> {
    try {
        const { data } = await client.POST("/bff/subscription/protect-profile/region", {
            body: { region: toProtectRegion(region) }
        });

        if (data?.region) {
            return { region: fromProtectRegion(data.region), wasAccepted: true };
        }
    } catch (error) {
        const conflictRegion = getConflictRegion(error);

        if (conflictRegion) {
            return { region: conflictRegion, wasAccepted: false };
        }

        throw error;
    }

    throw new Error("Failed to set Protect region");
}
